// olap-client/src/components/chat/ChatApp.jsx
import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Context } from '../..';
import LeftSidebar from './LeftSidebar';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import ChatSettings from './ChatSettings';
import BottomNav from './BottomNav';
import MobileChatList from './MobileChatList';
import ContactsTab from './ContactsTab';
import SettingsTab from './SettingsTab';
import ProfileTab from './ProfileTab';
import MessageContextMenu from '../MessageContextMenu';
import ForwardModal from '../ForwardModal';
import { ensurePushSubscription } from '../../utils/pushNotifications';

const ChatApp = observer(() => {
  const { user, chat } = useContext(Context);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  // === ОПРЕДЕЛЕНИЕ PWA РЕЖИМА (при монтировании) ===
  useEffect(() => {
    const standalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    
    setIsStandalone(standalone);
    console.log('📱 Standalone mode:', standalone);
  }, []);

  // === АВТООТКРЫТИЕ ПРИ ЛОГИНЕ В PWA РЕЖИМЕ ===
  useEffect(() => {
    if (user.isAuth && isStandalone) {
      const token = localStorage.getItem('token');
      if (token && !chat.isWidgetOpen) {
        console.log('🚀 [ChatApp] User logged in in PWA — auto-opening messenger...');
        chat.openWidget();
      }
    }
  }, [user.isAuth, isStandalone, chat]);

  // === ОСНОВНОЙ useEffect: подключение, чаты, push ===
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuth = user.isAuth || !!token;
    
    console.log('🔍 [ChatApp] Main useEffect triggered', {
      isAuth,
      userIsAuth: user.isAuth,
      hasToken: !!token,
      isStandalone
    });

    if (isAuth && token) {
      console.log('✅ [ChatApp] User authenticated, starting services...');
      
      chat.connect();
      chat.loadChats();
      
      console.log('🔔 [ChatApp] Calling ensurePushSubscription...');
      ensurePushSubscription()
        .then(result => {
          console.log('✅ [ChatApp] ensurePushSubscription result:', result);
        })
        .catch(err => {
          console.error('❌ [ChatApp] ensurePushSubscription error:', err);
        });
      
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('👁️ [ChatApp] App became visible, checking push...');
          ensurePushSubscription();
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      const intervalId = setInterval(() => {
        console.log('⏰ [ChatApp] Periodic push check...');
        ensurePushSubscription();
      }, 30 * 60 * 1000);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        clearInterval(intervalId);
      };
    } else {
      console.log('⚠️ [ChatApp] User NOT authenticated, skipping push setup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuth]);

  // === PWA install prompt ===
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('✅ PWA install prompt captured');
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) {
      alert('❌ Установка недоступна');
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);
    setDeferredPrompt(null);
  };

  const token = localStorage.getItem('token');
  if (!user?.isAuth && !token) return null;
  if (!user?.isAuth) return null;

  return (
    <>
      <MessageContextMenu />
      <ForwardModal />

      {/* === КНОПКА 💬 — ТОЛЬКО В БРАУЗЕРЕ (не в PWA) === */}
      {!isStandalone && !chat.isWidgetOpen && (
        <button
          onClick={() => chat.openWidget()}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#3390ec',
            color: 'white',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Открыть мессенджер"
        >
          💬
          {chat.totalUnread > 0 && (
            <span style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              background: '#ff3b30',
              color: 'white',
              borderRadius: '50%',
              minWidth: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '2px solid white'
            }}>
              {chat.totalUnread > 99 ? '99+' : chat.totalUnread}
            </span>
          )}
        </button>
      )}

      {/* === ИНТЕРФЕЙС МЕССЕНДЖЕРА === */}
      {chat.isWidgetOpen && (
        <>
          {!chat.isMobile && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 9999,
              fontFamily: 'Arial, sans-serif',
              display: 'flex'
            }}>
              <LeftSidebar />
              <ChatList />
              
              {chat.activeChat && (
                <>
                  <ChatWindow />
                  <ChatSettings />
                </>
              )}

              {!chat.activeChat && (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f4f4f5',
                  color: '#707579',
                  fontSize: '16px'
                }}>
                  Выберите чат для начала общения
                </div>
              )}

              {/* === КНОПКА ✕ — ТОЛЬКО В БРАУЗЕРЕ === */}
              {!isStandalone && (
                <button
                  onClick={() => chat.closeWidget()}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '10px',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#707579',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                    zIndex: 10001
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f4f4f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  title="Закрыть мессенджер"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {chat.isMobile && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 9999,
              fontFamily: 'Arial, sans-serif',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {chat.activeChat ? (
                <ChatWindow />
              ) : (
                <>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    {chat.mobileTab === 'chats' && <MobileChatList />}
                    {chat.mobileTab === 'contacts' && <ContactsTab />}
                    {chat.mobileTab === 'settings' && <SettingsTab />}
                    {chat.mobileTab === 'profile' && <ProfileTab />}
                  </div>
                  <BottomNav />
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* === КНОПКА УСТАНОВКИ — ТОЛЬКО В БРАУЗЕРЕ === */}
      {!isStandalone && deferredPrompt && (
        <button
          onClick={handleInstallApp}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            padding: '12px 20px',
            background: '#3390ec',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 9998
          }}
        >
          📱 Установить приложение
        </button>
      )}
    </>
  );
});

export default ChatApp;